import { ColDef, NewValueParams } from '@ag-grid-community/core';
import { DbSaveService } from '@data';
import { ActiveStatusEnum, Guideline, GuidelineSeverityEnum } from '@models';

import { AgFns } from '../components/ag-grid/ag-fns';

/** The level of an override. The chain is always master -> supplier -> account -> program. */
export type GuidelineLevel = 'supplier' | 'account' | 'program';

/** The one scope a page makes overrides for. A program also names its account, for the level above it. */
export interface GuidelineScope {
  level: GuidelineLevel;
  id: string;
  accountId?: string;
}

/** One grid row: one master. Its overrides are read from master.overrides, so undo and save need no rebuild. */
export interface GuidelineOverrideRow {
  master: Guideline;
}

const STATUS_NAMES: Record<number, string> = { [ActiveStatusEnum.Active]: 'Active', [ActiveStatusEnum.Inactive]: 'Inactive' };
const SEVERITY_NAMES: Record<number, string> = {
  [GuidelineSeverityEnum.Block]: 'Block',
  [GuidelineSeverityEnum.Warn]: 'Warn',
  [GuidelineSeverityEnum.Off]: 'Off',
};
const LEVEL_ABOVE = 'Level above';

/**
 * The AI Guidelines grid of one scope: the Program form tab, and the Account and Supplier pages.
 *
 * Nobody here writes a new rule. Each row is a master from Utilities > AI Guidelines, and this
 * page can only override it, once, for its own scope. An override always points to the master;
 * the order comes from the level (master -> supplier -> account -> program). An override AMENDS
 * the level above it: the AI reads the whole chain and the narrowest level wins where they differ.
 *
 * SEVERITY works the same way: Block or Warn on the master, and an override may change it for its
 * scope or keep the level above. An override may change only the severity and leave the text alone.
 * Off (account and program overrides only) means the rule does not apply to that scope; a program
 * override can turn an account's Off back on with Block or Warn.
 */
export class GuidelineOverrideGrid {
  rows: GuidelineOverrideRow[] = [];

  constructor(
    private dbSaveService: DbSaveService,
    private scope: () => GuidelineScope,
    private isReadOnly: () => boolean,
  ) {}

  /** One row per master, from the guidelines an endpoint returned (masters and overrides). */
  setGuidelines(guidelines: Guideline[]) {
    this.rows = guidelines
      .filter((g) => !g.masterGuidelineId)
      .sort((a, b) => GuidelineOverrideGrid.ruleName(a).localeCompare(GuidelineOverrideGrid.ruleName(b)))
      .map((master) => ({ master }));
  }

  /** The name a rule shows in grids and messages. */
  static ruleName(g: Guideline): string {
    if (g.title?.trim()) return g.title.trim();
    const text = (g.instruction ?? '').trim();
    return text ? (text.length > 60 ? text.slice(0, 57) + '...' : text) : '(new rule)';
  }

  /** This scope's override of the row's master, if there is one. */
  own(row: GuidelineOverrideRow): Guideline | undefined {
    const s = this.scope();
    return this.overridesOf(row).find((o) => this.idAt(o, s.level) === s.id);
  }

  /** The overrides above this scope: supplier overrides, then (for a program) its account's override. */
  above(row: GuidelineOverrideRow): Guideline[] {
    const s = this.scope();
    if (s.level === 'supplier') return [];
    const suppliers = this.overridesOf(row).filter((o) => !!o.supplierId);
    const account = s.level === 'program' ? this.overridesOf(row).filter((o) => o.accountId && o.accountId === s.accountId) : [];
    return [...suppliers, ...account];
  }

  /** On an account page: the overrides of the account's programs, which sit under its override. */
  below(row: GuidelineOverrideRow): Guideline[] {
    const s = this.scope();
    if (s.level !== 'account') return [];
    return this.overridesOf(row).filter((o) => o.programId && o.program?.accountId === s.id);
  }

  colDefs(): ColDef<GuidelineOverrideRow>[] {
    const level = this.scope().level;
    // An edit makes, changes or retires the override, so Status and In Effect change with it.
    const refreshRow = (p: NewValueParams<GuidelineOverrideRow>) => p.api.refreshCells({ rowNodes: [p.node!], force: true });
    const ownEditable = (row: GuidelineOverrideRow) =>
      !this.isReadOnly() && row.master.activeStatusId !== ActiveStatusEnum.Inactive;
    const cols: ColDef<GuidelineOverrideRow>[] = [
      {
        headerName: 'Master',
        colId: 'master',
        valueGetter: (p) => (p.data ? GuidelineOverrideGrid.ruleName(p.data.master) : ''),
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(180),
      },
      {
        headerName: 'Master Instruction',
        colId: 'masterInstruction',
        valueGetter: (p) => p.data?.master.instruction ?? '',
        tooltipValueGetter: (p) => p.data?.master.instruction ?? '',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(380),
      },
    ];
    if (level !== 'supplier') {
      cols.push({
        headerName: 'Overrides Above',
        colId: 'above',
        valueGetter: (p) => (p.data ? this.describe(this.above(p.data)) : ''),
        tooltipValueGetter: (p) => (p.data ? this.describe(this.above(p.data)) : ''),
        ...AgFns.flexWidth(240),
      });
    }
    cols.push(
      {
        headerName: `${this.levelName(level)} Override`,
        colId: 'own',
        editable: (p) => !!p.data && ownEditable(p.data),
        valueGetter: (p) => (p.data ? (this.own(p.data)?.instruction ?? '') : ''),
        valueSetter: (p) => this.setOwnText(p.data, (p.newValue ?? '') as string),
        tooltipValueGetter: (p) => (p.data ? (this.own(p.data)?.instruction ?? '') : ''),
        headerTooltip: 'Leave it empty to keep the text of the level above, for example to change only the severity.',
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: { maxLength: 8000, rows: 12, cols: 80 },
        onCellValueChanged: refreshRow,
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(380),
      },
      {
        headerName: 'Severity',
        colId: 'ownSeverity',
        editable: (p) => !!p.data && ownEditable(p.data),
        valueGetter: (p) => {
          const own = p.data ? this.own(p.data) : undefined;
          return own?.guidelineSeverityId ? SEVERITY_NAMES[own.guidelineSeverityId] : LEVEL_ABOVE;
        },
        valueSetter: (p) => this.setOwnSeverity(p.data, p.newValue as string),
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: { values: level === 'supplier' ? [LEVEL_ABOVE, 'Block', 'Warn'] : [LEVEL_ABOVE, 'Block', 'Warn', 'Off'] },
        headerTooltip:
          'Block: in Enforce mode, an open finding stops activation. Warn: a finding never stops it.' +
          (level === 'supplier' ? '' : ` Off: the rule does not apply to this ${level}, and the AI does not check it.`),
        onCellValueChanged: refreshRow,
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Status',
        colId: 'ownStatus',
        editable: (p) => !!p.data && ownEditable(p.data) && !!this.own(p.data),
        valueGetter: (p) => {
          const own = p.data ? this.own(p.data) : undefined;
          return own ? STATUS_NAMES[own.activeStatusId] : '';
        },
        valueSetter: (p) => {
          const own = this.own(p.data);
          if (!own) return false;
          own.activeStatusId = p.newValue === 'Inactive' ? ActiveStatusEnum.Inactive : ActiveStatusEnum.Active;
          return true;
        },
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: { values: ['Active', 'Inactive'] },
        onCellValueChanged: refreshRow,
        ...AgFns.fixWidth(100),
      },
    );
    if (level === 'account') {
      cols.push({
        headerName: 'Program Overrides Below',
        colId: 'below',
        valueGetter: (p) => (p.data ? this.describe(this.below(p.data)) : ''),
        tooltipValueGetter: (p) => (p.data ? this.describe(this.below(p.data)) : ''),
        ...AgFns.flexWidth(200),
      });
    }
    cols.push({
      headerName: 'In Effect',
      colId: 'inEffect',
      valueGetter: (p) => (p.data ? this.inEffect(p.data) : ''),
      tooltipValueGetter: (p) => (p.data ? this.inEffect(p.data) : ''),
      ...AgFns.flexWidth(180),
    });
    return cols;
  }

  /** What the AI reads for this scope - the master, then each active override down to this one - and the severity in effect. */
  inEffect(row: GuidelineOverrideRow): string {
    if (row.master.activeStatusId === ActiveStatusEnum.Inactive) return 'No - the master is Inactive';
    const own = this.own(row);
    const chain = [...this.above(row), ...(own ? [own] : [])].filter((o) => o.activeStatusId !== ActiveStatusEnum.Inactive);
    // The narrowest level that sets a severity wins; a master always has one.
    const severity = [...chain].reverse().find((o) => o.guidelineSeverityId)?.guidelineSeverityId ?? row.master.guidelineSeverityId;
    if (severity === GuidelineSeverityEnum.Off) return 'No - Off';
    const levels = ['Master', ...chain.filter((o) => (o.instruction ?? '').trim()).map((o) => this.levelLabel(o))].join(' + ');
    return `${levels} - ${severity ? SEVERITY_NAMES[severity] : 'Warn'}`;
  }

  /** What stops a save. Empty = it may save. */
  problems(): string[] {
    const out: string[] = [];
    for (const row of this.rows) {
      const own = this.own(row);
      if (own && !own.entityAspect.entityState.isUnchanged() && !(own.instruction ?? '').trim() && !own.guidelineSeverityId) {
        out.push(
          `${GuidelineOverrideGrid.ruleName(row.master)}: the override changes nothing. Give it a text or a severity. To retire it, set its Status to Inactive.`,
        );
      }
    }
    return out;
  }

  /**
   * Account page only: the changed account overrides that have program overrides under them.
   * A warning, not a refusal: the admin checks those program overrides, and the save continues.
   */
  warnings(): string[] {
    const out: string[] = [];
    for (const row of this.rows) {
      const own = this.own(row);
      if (!own || own.entityAspect.entityState.isUnchanged()) continue;
      const below = this.below(row).filter((o) => o.activeStatusId !== ActiveStatusEnum.Inactive);
      if (below.length) {
        const names = below.map((o) => o.program?.name ?? '?').join(', ');
        out.push(
          `${GuidelineOverrideGrid.ruleName(row.master)}: ${below.length} program override(s) sit under this override (${names}). Check them.`,
        );
      }
    }
    return out;
  }

  /** Typing text makes this scope's override; clearing the text of an unsaved override that changes nothing else removes it. */
  private setOwnText(row: GuidelineOverrideRow, text: string): boolean {
    const own = this.own(row);
    const trimmed = text.trim();
    if (!own) {
      if (!trimmed) return false;
      this.createOwn(row, { instruction: trimmed });
      return true;
    }
    if (!trimmed && !own.guidelineSeverityId && own.entityAspect.entityState.isAdded()) {
      own.entityAspect.setDetached();
      return true;
    }
    // Empty text = the text of the level above (null, not '': the server stores no text).
    own.instruction = trimmed || undefined;
    return true;
  }

  /** Choosing Block, Warn or Off makes this scope's override if there is none; Level above clears it. */
  private setOwnSeverity(row: GuidelineOverrideRow, value: string): boolean {
    const own = this.own(row);
    const found = Object.keys(SEVERITY_NAMES).find((k) => SEVERITY_NAMES[Number(k)] === value);
    let severity = found ? (Number(found) as GuidelineSeverityEnum) : undefined;
    // A supplier override may not turn a rule off (CK_Guidelines_OffOnAccountOrProgramOnly).
    if (severity === GuidelineSeverityEnum.Off && this.scope().level === 'supplier') severity = undefined;
    if (!own) {
      if (!severity) return false;
      this.createOwn(row, { guidelineSeverityId: severity });
      return true;
    }
    if (!severity && !(own.instruction ?? '').trim() && own.entityAspect.entityState.isAdded()) {
      own.entityAspect.setDetached();
      return true;
    }
    own.guidelineSeverityId = severity;
    return true;
  }

  private createOwn(row: GuidelineOverrideRow, values: Partial<Guideline>) {
    const s = this.scope();
    this.dbSaveService.createEntity(Guideline, {
      guidelineContextId: row.master.guidelineContextId,
      masterGuidelineId: row.master.id,
      [`${s.level}Id`]: s.id,
      activeStatusId: ActiveStatusEnum.Active,
      ...values,
    } as Partial<Guideline>);
  }

  private overridesOf(row: GuidelineOverrideRow): Guideline[] {
    return (row.master.overrides ?? []).filter((o) => !o.entityAspect.entityState.isDetached());
  }

  private idAt(o: Guideline, level: GuidelineLevel): string | undefined {
    return level === 'supplier' ? o.supplierId : level === 'account' ? o.accountId : o.programId;
  }

  private levelName(level: GuidelineLevel) {
    return level === 'supplier' ? 'Supplier' : level === 'account' ? 'Account' : 'Program';
  }

  private levelLabel(o: Guideline): string {
    if (o.supplierId) return `Supplier ${o.supplier?.name ?? ''}`.trim();
    if (o.accountId) return 'Account';
    return o.program?.name ? `Program ${o.program.name}` : 'Program';
  }

  private describe(overrides: Guideline[]): string {
    return overrides
      .map((o) => {
        const sev = o.guidelineSeverityId ? ` [${SEVERITY_NAMES[o.guidelineSeverityId]}]` : '';
        const text = (o.instruction ?? '').trim() || '(text of the level above)';
        return `${this.levelLabel(o)}${o.activeStatusId === ActiveStatusEnum.Inactive ? ' (Inactive)' : ''}${sev}: ${text}`;
      })
      .join(' | ');
  }
}
